"use client";
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  Handshake, 
  ShoppingBag, 
  CheckCircle2, 
  Phone, 
  Globe, 
  Mail, 
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Construction
} from 'lucide-react';

// مكون فرعي للكارت لضمان استقلالية زر "عرض المزيد" لكل خدمة
const ServiceCard = ({ data, icon: Icon, colorClass, delay = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div 
      initial={{ y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.5 }}
      className="bg-white/80 backdrop-blur-md border border-slate-100 rounded-[2rem] sm:rounded-[2.5rem] shadow-[0_15px_40px_-15px_rgba(0,0,0,0.03)] hover:shadow-[0_30px_60px_-15px_rgba(0,74,173,0.12)] flex flex-col justify-between h-full transition-all duration-500 group/card overflow-hidden w-full relative z-10"
    >
      {/* محتوى الكارت والنصوص */}
      <div className="p-5 sm:p-6 md:p-8 pt-6 sm:pt-8 md:pt-10 space-y-4 md:space-y-5 flex-1 flex flex-col">
        
        {/* بلوك الصورة المطور - تم جعل الصورة Fill بالكامل */}
        <div className="w-full h-40 sm:h-48 md:h-52 rounded-[1.5rem] sm:rounded-[1.8rem] shadow-[0_8px_30px_rgb(0,0,0,0.02)] border border-slate-100/80 overflow-hidden relative transition-all duration-500 flex items-center justify-center">
          <img 
            src={data.imageSrc} 
            alt={data.title} 
            className="w-full h-full object-cover rounded-[1.5rem] sm:rounded-[1.8rem] transition-transform duration-500 group-hover/card:scale-105"
          />
        </div>

        {/* هيدر العنوان والأيقونة الفرعية */}
        <div className="flex items-center gap-2.5 sm:gap-3 pt-1">
          <div className={`p-2 sm:p-2.5 rounded-xl ${colorClass.bg} ${colorClass.text} shrink-0 shadow-sm`}>
            <Icon className="w-4 h-4 sm:w-5 h-5" />
          </div>
          <h3 className="text-lg sm:text-xl font-black text-gray-900 tracking-tight transition-colors duration-300 group-hover/card:text-[#004aad]">
            {data.title}
          </h3>
        </div>
        
        {/* وصف الخدمة */}
        <p className="text-gray-600 text-xs sm:text-[14px] leading-relaxed font-medium text-right flex-1">
          {data.desc}
        </p>

        {/* زر التمدد المطور والمثير للانتباه */}
        <button 
          onClick={() => setIsExpanded(!isExpanded)}
          className={`group/btn flex items-center gap-3 text-xs sm:text-sm font-black px-4 py-2.5 rounded-xl border transition-all duration-300 w-fit pt-2 focus:outline-none cursor-pointer shadow-sm relative overflow-hidden
            ${isExpanded 
              ? 'bg-slate-100 border-slate-200 text-slate-700' 
              : `${colorClass.lightBg} border-current/10 ${colorClass.text} hover:border-current/30 hover:shadow-[0_4px_12px_rgba(0,0,0,0.03)]`
            }`}
        >
          <span>{isExpanded ? "عرض تفاصيل أقل" : "عرض المزيد من التفاصيل"}</span>
          
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 transition-transform duration-300 group-hover/btn:-translate-y-0.5" />
          ) : (
            <motion.div
              animate={{ y: [0, 3, 0] }}
              transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
            >
              <ChevronDown className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-y-0.5" />
            </motion.div>
          )}
        </button>

        {/* تفاصيل القائمة المنسدلة التفاعلية */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: "easeInOut" }}
              className="overflow-hidden space-y-4 sm:space-y-5 pt-2 sm:pt-3"
            >
              <div className="space-y-2.5 sm:space-y-3">
                <h4 className={`text-xs sm:text-[14px] font-black ${colorClass.text} border-r-2 border-current pr-2`}>{data.subTitle}</h4>
                <ul className="grid grid-cols-1 gap-2">
                  {data.features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-gray-600 text-xs sm:text-sm leading-relaxed bg-slate-50/60 p-2.5 sm:p-3 rounded-xl sm:rounded-2xl border border-slate-100/50">
                      <CheckCircle2 className={`w-3.5 h-3.5 ${colorClass.text} shrink-0 mt-0.5`} />
                      <span>{feat.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {data.extraSection && (
                <div className={`p-3.5 sm:p-4 rounded-xl sm:rounded-2xl space-y-2 border border-slate-100/40 ${colorClass.lightBg}`}>
                  <h4 className="text-[11px] sm:text-xs font-black text-gray-950">{data.extraSection.title}</h4>
                  <div className="grid grid-cols-1 gap-1.5">
                    {data.extraSection.items.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-[11px] sm:text-xs text-gray-600 font-bold">
                        <ArrowLeft className="w-3 h-3 text-gray-400 shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* الديف السفلي: بيانات الاتصال */}
      <div className="px-5 sm:px-6 md:px-8 pb-5 sm:pb-6 md:pb-8 pt-3.5 border-t border-slate-100 bg-slate-50/40 space-y-3">
        <div className="text-[11px] sm:text-xs font-bold text-gray-800 text-right">
          <p className="text-gray-900 mb-0.5 font-black">{data.contactTitle}</p>
          <p className="text-gray-500 font-medium text-[10px] sm:text-[11px]">{data.contactDesc}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-[10px] sm:text-[11px] font-bold">
          
          {/* رابط الهاتف */}
          <a 
            href={`tel:${data.phone}`}
            className={`flex items-center justify-between gap-2 px-2.5 py-2 sm:px-3 sm:py-2.5 rounded-xl border border-transparent transition-all duration-300 ${colorClass.lightBg} ${colorClass.text} hover:opacity-80 active:scale-[0.98] shadow-sm`}
          >
            <span className="flex-1 text-right" dir="ltr">{data.phone}</span>
            <Phone className="w-3.5 h-3.5 shrink-0" />
          </a>

          {/* رابط الموقع الإلكتروني */}
          <a 
            href={`https://${data.web}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between gap-2 bg-white text-gray-600 px-2.5 py-2 sm:px-3 sm:py-2.5 rounded-xl border border-slate-100 hover:border-[#004aad]/30 hover:text-[#004aad] transition-all overflow-hidden shadow-sm active:scale-[0.98] group"
          >
            <span className="truncate flex-1 text-right" dir="ltr">{data.web}</span>
            <Globe className="w-3.5 h-3.5 text-gray-400 shrink-0 group-hover:text-[#004aad]" />
          </a>

          {/* رابط البريد الإلكتروني */}
          <a 
            href={`mailto:${data.email}`}
            className="flex items-center justify-between gap-2 bg-white text-gray-600 px-2.5 py-2 sm:px-3 sm:py-2.5 rounded-xl border border-slate-100 hover:border-[#004aad]/30 hover:text-[#004aad] transition-all overflow-hidden shadow-sm active:scale-[0.98]"
          >
            <span className="truncate flex-1 text-right" dir="ltr">{data.email}</span>
            <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          </a>

        </div>
      </div>
    </motion.div>
  );
};

export default function OtherServices() {
  const services = [
    {
      id: "agents",
      icon: Users,
      color: { bg: "bg-blue-50", text: "text-[#004aad]", lightBg: "bg-blue-50/15" },
      data: {
        title: "وكلاء ليزرجيت مصر (LaserJet Agents)",
        imageSrc: "/Partners.jpeg", 
        desc: "شراكة عمل متكاملة تمنحك حق تمثيل 'ليزرجيت' في منطقتك الجغرافية، لتكون جزءاً من شبكة وكلاءنا المعتمدين في مصر.",
        subTitle: "لماذا تصبح وكيلاً معتمداً لـ 'ليزرجيت'؟",
        features: [
          { text: "تمثيل العلامة التجارية الرائدة في قطاع الأجهزة المنزلية." },
          { text: "امتيازات حصرية وأسعار خاصة وأولوية في المنتجات الجديدة." },
          { text: "دعم تسويقي شامل ومواد دعائية احترافية." },
          { text: "تدريب وتطوير مستمر لفريق عملك إدارياً وفنياً." },
          { text: "نظام إمداد وتوريد قوي يضمن سرعة الشحن وتوافر المخزون." },
          { text: "إدارة مخصصة لمتابعة عملياتك وتذليل التحديات." }
        ],
        extraSection: {
          title: "شروط الانضمام لشبكة الوكلاء",
          items: ["الالتزام بالهوية البصرية للماركة", "توفر سجل تجاري ومقر نشاط", "القدرة على تحقيق مستهدفات البيع"]
        },
        contactTitle: "كن شريك نجاحنا اليوم",
        contactDesc: "تواصل معنا لتقديم طلب الحصول على وكالة معتمدة:",
        phone: "01004331113", web: "agents.laserjet.com.eg", email: "agents@laserjet.com.eg"
      }
    },
    {
      id: "partners",
      icon: Handshake,
      color: { bg: "bg-emerald-50", text: "text-emerald-700", lightBg: "bg-emerald-50/15" },
      data: {
        title: "شركاء ليزرجيت (LaserJet Partners)",
        imageSrc: "/Partners.jpeg", 
        desc: "فرصة استثمارية واعدة لتحقيق عوائد مادية من خلال تسويق منتجات 'ليزرجيت'. انضم لشبكة تمنحك عمولة مجزية.",
        subTitle: "لماذا تنضم لبرنامج 'شركاء ليزرجيت'؟",
        features: [
          { text: "عائد مادي تنافسي وعمولات مجزية عن كل عملية بيع." },
          { text: "حرية العمل الكاملة وتحديد أوقاتك بما يناسبك." },
          { text: "الترويج لمنتجات متنوعة ذات جودة عالية وطلب مرتفع." },
          { text: "أدوات تسويقية متطورة تساعدك في الوصول لجمهورك." },
          { text: "لوحة تحكم شفافة لمتابعة المبيعات والعمولات لحظياً." },
          { text: "دعم مستمر من فريق مخصص لحل التحديات التسويقية." }
        ],
        extraSection: {
          title: "كيف تبدأ؟",
          items: ["التسجيل عبر الموقع الإلكتروني", "الترويج عبر روابطك المخصصة", "تحقيق الربح فور إتمام البيع"]
        },
        contactTitle: "للتواصل مع إدارة برنامج الشركاء",
        contactDesc: "هل أنت مستعد لبدء مسيرة نجاحك معنا？",
        phone: "01004331113", web: "partners.laserjet.com.eg", email: "partners@laserjet.com.eg"
      }
    },
    {
      id: "service",
      icon: Construction,
      color: { bg: "bg-slate-100", text: "text-slate-700", lightBg: "bg-slate-100/50" },
      data: {
        title: "ليزرجيت سيرفيس (LaserJet Service)",
        imageSrc: "/serves.jpeg", 
        desc: "حلول متكاملة للتأسيس والتشطيب، نقدم إدارة شاملة للمشروعات السكنية التجارية من التخطيط حتى التسليم 'على المفتاح'.",
        subTitle: "مجالات الخدمة الرئيسية",
        features: [
          { text: "التأسيس والتركيبات الفنية والتقنية بدقة عالية." },
          { text: "التشطيبات المتكاملة والديكور وفق أحدث الطرز." },
          { text: "إدارة المشروعات بالكامل لتوفير عناء تعدد المقاولين." },
          { text: "ضمان حقيقي على كافة أعمال التركيبات والتشطيب." },
          { text: "التسليم النهائي (على المفتاح) مطابقاً للمواصفات." }
        ],
        extraSection: {
          title: "لماذا تختار 'ليزرجيت سيرفيس'؟",
          items: ["المسؤولية الكاملة تحت سقف واحد", "الالتزام بالجدول الزمني للتسليم", "احترافية عالية من طاقم فني متخصص"]
        },
        contactTitle: "ابدأ مشروعك اليوم",
        contactDesc: "فريقنا الهندسي جاهز لمعاينة موقعك وتقديم الاستشارة:",
        phone: "01004331113", web: "service.laserjet.com.eg", email: "service@laserjet.com.eg"
      }
    },
    {
      id: "sell",
      icon: ShoppingBag,
      color: { bg: "bg-purple-50", text: "text-purple-700", lightBg: "bg-purple-50/15" },
      data: {
        title: "منصة التاجر الرقمي (Sell)",
        imageSrc: "/sell.jpeg", 
        desc: "شراكة استراتيجية تتيح للتجار والشركات الوصول لقاعدة عملاء واسعة عبر منصة 'ليزرجيت' مع دعم لوجستي كامل.",
        subTitle: "مميزات الانضمام لخدمة 'Sell'",
        features: [
          { text: "قاعدة عملاء واسعة تضمن انتشار علامتك التجارية." },
          { text: "لوحة تحكم ذكية لإدارة المنتجات والمخزون والأسعار." },
          { text: "نظام تقارير وتحليلات دقيقة لسلوك العملاء والمبيعات." },
          { text: "حلول لوجستية متطورة للتخزين والشحن السريع." },
          { text: "دعم فني وتدريب لفريق عملك على استخدام المنصة." },
          { text: "بوابات دفع آمنة تضمن سرعة التحصيل المالي." }
        ],
        extraSection: {
          title: "لماذا تختار 'Sell' كشريك رقمي؟",
          items: ["تواجد رقمي فعال بدون استثمارات تقنية ضخمة", "إدارة احترافية للمهام التقنية واللوجستية", "نمو مستدام والوصول لشرائح سوقية جديدة"]
        },
        contactTitle: "انضم إلى 'Sell' اليوم",
        contactDesc: "للمشاركة في المنصة والبدء في عرض منتجاتك:",
        phone: "01004331113", web: "sell.laserjet.com.eg", email: "sell@laserjet.com.eg"
      }
    }
  ];

  return (
    <section 
      id="other-services" 
      className="w-full bg-slate-50/60 py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8 font-sans relative overflow-hidden bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:4rem_4rem]" 
      dir="rtl"
    >
      {/* عناصر الديكور */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <motion.div 
          animate={{ x: [0, 40, 0], y: [0, -30, 0] }}
          transition={{ repeat: Infinity, duration: 12, ease: "easeInOut" }}
          className="absolute -top-40 -right-40 w-96 h-96 bg-blue-100/40 rounded-full blur-[100px]"
        />
        <motion.div 
          animate={{ x: [0, -40, 0], y: [0, 40, 0] }}
          transition={{ repeat: Infinity, duration: 15, ease: "easeInOut" }}
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-100/30 rounded-full blur-[100px]"
        />
      </div>

      <div className="max-w-7xl mx-auto space-y-10 md:space-y-20 relative z-10">
        
        {/* العنوان الرئيسي */}
        <div className="text-center max-w-2xl mx-auto space-y-2 md:space-y-4">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-gray-900 tracking-tight leading-tight sm:leading-none">
            خدمات وحلول <span className="text-[#004aad]">ليزرجيت</span> المتكاملة
          </h2>
          <p className="text-[11px] sm:text-xs md:text-sm text-gray-500 font-bold max-w-md mx-auto leading-relaxed px-2 sm:px-0">
            اكتشف بوابات الاستثمار والتمثيل التجاري الرقمي الأقوى والأكثر موثوقية داخل مصر
          </p>
          <div className="w-16 sm:w-24 h-1 bg-gradient-to-r from-transparent via-[#004aad] to-transparent mx-auto rounded-full mt-2" />
        </div>

        {/* شبكة العرض */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-stretch">
          {services.map((service, index) => (
            <div key={service.id} className="flex w-full">
              <ServiceCard 
                data={service.data}
                icon={service.icon}
                colorClass={service.color}
                delay={index * 0.08}
              />
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}